import type { ContentHistoryParams, HistoryTargetParams } from '../activity/content-history-types'
import type { CatalogParams } from '../translations/types'
/**
 * Every query key in `apps/admin`, in one place (ADR-0056).
 *
 * Keys are **hierarchical and client-first**, because that is what makes
 * invalidation expressible. TanStack Query matches keys by prefix, so
 * `invalidateQueries({ queryKey: keys.client(id) })` reaches the client
 * detail, its cohort list, and every cohort under it — which is exactly the
 * set a cohort edit makes stale, and exactly the set the hand-rolled
 * `useEffect` refetching used to miss.
 *
 *   ['clients']                                    everything client-ish
 *   ['clients', 'list', params]                    the searched/paged list
 *   ['clients', id]                                one client
 *   ['clients', id, 'cohorts']                     its cohorts
 *   ['clients', id, 'cohorts', cohortId]           one cohort
 *   ['cohorts', 'list', params]                     the GLOBAL cohort list
 *   ['students', 'list', params]                    the roster, however filtered
 *   ['students', id]                                one student
 *   ['hr-users', 'list', params]                    the GLOBAL HR directory
 *
 * `'list'` is safe as a discriminator beside an id because every id here is
 * a UUID. Params go **inside** the key rather than beside it so two
 * different filter sets are two different cache entries — that is what
 * makes the roster's `?clientId=` preview and the full list coexist
 * without either serving the other's rows.
 *
 * The rule for a new module: add keys here, never a bare array at a call
 * site. A key written inline is a key nothing can invalidate, and a stale
 * view is quieter than a failed fetch.
 */
import type { ListClientsParams } from '../clients/types.js'
import type { InvalidateQueryFilters } from '@tanstack/react-query'
import type { ListCohortsParams } from '../cohorts/types.js'
import type { ListStudentsParams } from '../students/types.js'
import type { ListHrUsersParams } from '../hr_users/types.js'
import type { ListCoooriAdminsParams } from '../admins/types.js'
import type { ListSuppressionsParams } from '../email_suppressions/types.js'
import type { ListJobAttemptsParams, ListJobDefinitionsParams } from '../jobs/types.js'
import type { EntityActivityParams, MyActivityParams } from '../activity/types.js'

export const queryKeys = {
  deviceEnvironments: (params: import('../devices/api').DeviceParams) => ['device-environments', 'list', params] as const,
  feedbackList: (params: object) => ['feedback', 'list', params] as const,
  feedbackDetail: (id: string) => ['feedback', 'detail', id] as const,
  contentCatalog: ['content-catalog'] as const,
  contentCatalogDetails: ['content-catalog-detail'] as const,
  contentRevisionIdentity: (kind: string, identityId: string) => ['content-revisions', kind, identityId] as const,
  contentRevisions: (kind: string, identityId: string, page: number, limit: number) => ['content-revisions', kind, identityId, 'list', page, limit] as const,
  contentRevisionHistory: (kind: string, identityId: string, page: number, limit: number) => ['content-revisions', kind, identityId, 'history', page, limit] as const,
  contentRevision: (kind: string, identityId: string, revisionId: string) => ['content-revisions', kind, identityId, revisionId] as const,
  contentRevisionComparison: (kind: string, identityId: string, fromRevisionId: string, toRevisionId: string) => ['content-revisions', kind, identityId, 'compare', fromRevisionId, toRevisionId] as const,
  sessionList: (params: import('../sessions/api').SessionParams) => ['sessions', 'list', params] as const,
  sessionDetail: (id: string, userId?: string) => ['sessions', 'detail', id, userId] as const,
  sessionOptions: (kind: import('../sessions/api').OptionKind, params: { q?: string; id?: string }) => ['sessions', 'options', kind, params] as const,
  catalogList: (page: string, params: import('../content/catalog-types.js').CatalogListParams) => ['content-catalog', page, params] as const,
  content: ['content'] as const,
  contentDiagnostic: (programId: string) => ['content', 'diagnostic', programId] as const,
  contentTopicSource: (topicId: string) =>
    ['content', 'topics', topicId, 'source'] as const,
  /** The whole client subtree — cohorts included, by prefix. */
  clients: ['clients'] as const,
  /**
   * Every client list, whatever its filters.
   *
   * Distinct from `clients` on purpose. `clients` is a prefix of a
   * *detail* key too, so invalidating it after a PATCH would refetch the
   * very row the response already gave us. Naming the list subtree lets a
   * mutation seed the detail from its response and still refresh the list
   * behind it.
   */
  clientLists: ['clients', 'list'] as const,
  clientList: (params: ListClientsParams) =>
    ['clients', 'list', params] as const,
  client: (clientId: string) => ['clients', clientId] as const,
  hrUsers: ['hr-users'] as const,
  hrUserList: (params: ListHrUsersParams) =>
    ['hr-users', 'list', params] as const,
  hrUser: (clientId: string, userId: string) =>
    ['clients', clientId, 'hr-users', userId] as const,
  hrUserCohortAccess: (clientId: string, userId: string) =>
    ['clients', clientId, 'hr-users', userId, 'cohort-access'] as const,
  coooriAdmins: ['cooori-admins'] as const,
  coooriAdminLists: ['cooori-admins', 'list'] as const,
  coooriAdminList: (params: ListCoooriAdminsParams) =>
    ['cooori-admins', 'list', params] as const,
  coooriAdmin: (userId: string) => ['cooori-admins', userId] as const,
  clientCohorts: (clientId: string) =>
    ['clients', clientId, 'cohorts'] as const,
  cohort: (clientId: string, cohortId: string) =>
    ['clients', clientId, 'cohorts', cohortId] as const,
  cohortHrAccess: (clientId: string, cohortId: string) =>
    ['clients', clientId, 'cohorts', cohortId, 'hr-access'] as const,

  /**
   * The global cohort list is a separate root, not part of a client.
   * It spans clients, so no single client's subtree owns it — which
   * means a cohort mutation has to invalidate both.
   */
  cohorts: ['cohorts'] as const,
  cohortList: (params: ListCohortsParams) =>
    ['cohorts', 'list', params] as const,

  students: ['students'] as const,
  studentLists: ['students', 'list'] as const,
  studentList: (params: ListStudentsParams) =>
    ['students', 'list', params] as const,
  student: (userId: string) => ['students', userId] as const,
  studentEnrollments: (clientId: string, userId: string) =>
    ['clients', clientId, 'students', userId, 'enrollments'] as const,
  programs: ['content', 'programs'] as const,
  /** Advisory duplicate-email pre-check; debounced at the call site. */
  studentEmailAvailable: (email: string) =>
    ['students', 'email-available', email] as const,

  emailTemplates: ['email-test-send', 'templates'] as const,

  jobDefinitions: (params: ListJobDefinitionsParams) =>
    ['jobs', 'definitions', params] as const,
  jobAttempts: (params: ListJobAttemptsParams) =>
    ['jobs', 'attempts', params] as const,

  /**
   * Undeliverable addresses (email suppressions). Keyed by the address
   * DIGEST, not a row id — the API's whole surface is hash-addressed.
   * `'list'` is safe beside a digest discriminator because a digest is
   * 64 hex chars.
   */
  emailSuppressions: ['email-suppressions'] as const,
  emailSuppressionLists: ['email-suppressions', 'list'] as const,
  emailSuppressionList: (params: ListSuppressionsParams) =>
    ['email-suppressions', 'list', params] as const,
  emailSuppression: (emailHash: string) =>
    ['email-suppressions', emailHash] as const,

  /** The i18n key catalog for one target locale. */
  i18nKeys: (params: CatalogParams) => ['i18n-keys', params] as const,

  /**
   * Activity history (PROV-AUDIT). Read-only projections over the
   * append-only audit log — but "append-only" cuts the other way for
   * staleness: EVERY successful mutation writes an audit row, so every
   * mutation makes these views stale. That invalidation is stated once,
   * on the mutation cache in `api/query-client.ts`, rather than named
   * in each `useMutation` — a forgotten copy would be a detail page
   * whose Activity section omits the action the operator just took.
   */
  activity: ['activity'] as const,
  catalogDetail: (page: string, id: string) => ['content-catalog-detail', page, id] as const,
  contentHistory: (params: ContentHistoryParams) => ['activity', 'content-history', params] as const,
  historyTargets: (params: HistoryTargetParams) => ['content', 'history-targets', params] as const,
  activityMine: (params: MyActivityParams) =>
    ['activity', 'mine', params] as const,
  activityEntity: (params: EntityActivityParams) =>
    ['activity', 'entity', params] as const,
} as const

/**
 * What a mutation touching one client makes stale.
 *
 * Collected here rather than spelled out per page because the answer is a
 * property of the data, not of the page you happen to be on: a cohort edit
 * changes the client detail (member counts), the client's cohort list,
 * the global cohort list, and the roster (which renders a cohort name).
 * Getting this wrong in one place used to mean one stale view; getting it
 * right here fixes all of them.
 *
 * Note `studentLists`, not `students`. A mutation must not invalidate the
 * detail row whose fresh value it is holding: the PATCH response IS the new
 * row, so the owning page seeds it with `setQueryData` and the broad key
 * would throw that away and refetch. Invalidate what you did not just
 * receive.
 */
export function keysStaleAfterCohortChange(clientId: string) {
  return [
    queryKeys.client(clientId),
    // The client LISTS as well: rows carry joinable and enrollable cohort
    // counts. Cohort create/delete/window/default changes move those values;
    // a stale list could otherwise report the wrong create-form blocker.
    queryKeys.clientLists,
    queryKeys.cohorts,
    queryKeys.studentLists,
  ]
}

/**
 * What a mutation touching one student makes stale.
 *
 * The client detail carries `studentCount`; enrollment mutations separately
 * invalidate its `currentEnrollmentCountByTier` projection through
 * `queriesStaleAfterEnrollmentChange`.
 */
export function keysStaleAfterStudentChange(clientId: string | null) {
  const keys: (readonly unknown[])[] = [queryKeys.studentLists]
  if (clientId !== null) keys.push(queryKeys.client(clientId))
  return keys
}

/**
 * What an enrollment mutation makes stale.
 *
 * Enrollment rows have their own query, but they also drive the student
 * lifecycle/attention projection, the roster, client usage totals, and both
 * client-scoped and global cohort funnels. Each affected projection is named
 * explicitly so a lifecycle change does not needlessly reload unrelated
 * client data such as the authored curriculum catalog.
 *
 * Keep every enrollment mutation on this helper. Refreshing only the list of
 * periods leaves the lifecycle badge looking correct only after a full page
 * reload, which is especially misleading after cancel/end/replace.
 */
export function queriesStaleAfterEnrollmentChange(
  clientId: string,
  userId: string,
): InvalidateQueryFilters[] {
  return [
    { queryKey: queryKeys.student(userId), exact: true },
    {
      queryKey: queryKeys.studentEnrollments(clientId, userId),
      exact: true,
    },
    { queryKey: queryKeys.studentLists },
    { queryKey: queryKeys.client(clientId), exact: true },
    { queryKey: queryKeys.clientCohorts(clientId) },
    { queryKey: queryKeys.cohorts },
  ]
}

/**
 * What releasing one suppression makes stale.
 *
 * Always the suppression's own detail (the mutation response is a bare
 * receipt; the enriched row comes from refetching) and every list view.
 * Plus the resolved PERSON's detail — their page renders
 * `emailUndeliverable`, and the person's cache key depends on their
 * role: students key by user id, HR users by (clientId, userId), and an
 * operator has no person page to go stale. Deciding this at the call
 * site is how the HR page kept a stale banner after a release.
 */
export function keysStaleAfterSuppressionRelease(
  emailHash: string,
  user: { role: string; userId: string; clientId: string } | null | undefined,
) {
  const keys: (readonly unknown[])[] = [
    queryKeys.emailSuppression(emailHash),
    queryKeys.emailSuppressionLists,
  ]
  if (user != null) {
    if (user.role === 'hr') keys.push(queryKeys.hrUser(user.clientId, user.userId))
    else if (user.role === 'student') keys.push(queryKeys.student(user.userId))
    else if (user.role === 'admin') keys.push(queryKeys.coooriAdmin(user.userId))
  }
  return keys
}
